import { MOBILE_TABS, type MobileTab } from './MobileTabBar';

// ─────────────────────────────────────────────────────────────────────
// WHICH TAB A LINK OPENS (#1137).
//
// The pay-link mail sends a member to `…?native=1&club=<slug>&tab=reservations
// #mobile`, so they land on «Reservasjoner», where «Betal nå» is, rather than
// on «Hjem» with nothing to say where to go. Read from the address once, when
// the app mounts — the same moment `initialTab` is read.
//
// An ALLOW-LIST of the tabs the app has. Anything else — a typo, a tab that
// was renamed, a value somebody made up — is ignored and the app opens on its
// ordinary default, never on a blank screen.
// ─────────────────────────────────────────────────────────────────────

const TABS = new Set<string>(MOBILE_TABS.map((t) => t.id));

export function initialMobileTabFromLocation(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): MobileTab | undefined {
  try {
    const tab = new URLSearchParams(search).get('tab');
    return tab !== null && TABS.has(tab) ? (tab as MobileTab) : undefined;
  } catch {
    return undefined;
  }
}

// ─────────────────────────────────────────────────────────────────────
// WHICH BOOKING A PAY LINK WAS FOR (#1364).
//
// The pay-link mail also names the booking: `…&booking=<book_…>`. A link can
// stop working — staff change the price, which voids it, or withdraw it — and
// the member who opens it must then be TOLD so, not shown a list with the
// booking silently missing. This is only the reading of the address; whether
// the booking is still theirs is decided against their own bookings, once the
// club has loaded (`MobileReservations`).
//
// The id is shape-checked, not trusted: it is never sent anywhere from here.
// ─────────────────────────────────────────────────────────────────────

export interface PayLinkTarget {
  readonly bookingId: string;
  /** The club the link was for, lower-cased — or null if it named none. */
  readonly club: string | null;
}

// The engine mints `book_<ms>_<rand>`; older rows may differ, so only the shape is held.
const BOOKING_ID = /^[A-Za-z0-9_-]{1,100}$/;

export function payLinkFromLocation(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): PayLinkTarget | null {
  try {
    const params = new URLSearchParams(search);
    const id = params.get('booking');
    if (id === null || !BOOKING_ID.test(id)) return null;
    const club = params.get('club')?.trim().toLowerCase() || null;
    return { bookingId: id, club };
  } catch {
    return null;
  }
}
